<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { ref } from "vue"

import NarrowPage from "@/components/NarrowPage.vue"
import FormatPicker from "@/components/print/FormatPicker.vue"
import LabelForm from "@/components/print/LabelForm.vue"
import LabelPreview from "@/components/print/LabelPreview.vue"
import PrintItemPicker from "@/components/print/PrintItemPicker.vue"
import StickerPreview from "@/components/print/StickerPreview.vue"
import { useLabelPrinting } from "@/composables/useLabelPrinting"
import { useNavbar } from "@/composables/useNavbar"
import { useStickerGeneration } from "@/composables/useStickerGeneration"
import { ICON } from "@/lib/icons"
import { printPath } from "@/routes"
import type { PrintItem } from "@/types/models"
import type { PrintFormat } from "@/types/printing"

const { title } = useNavbar({ title: "Print" })

interface Props {
  item: PrintItem | null
  text: string
  include_qr: boolean
  default_style: string
  formats: PrintFormat[]
  tab: string
}

const props = defineProps<Props>()
const activeTab = ref(props.tab === "stickers" ? "stickers" : "label")
const tabItems = [
  { label: "Label", value: "label" },
  { label: "Asset Tags", value: "stickers" },
]

const label = useLabelPrinting({
  item: props.item,
  formats: props.formats,
  initialText: props.text,
  initialQr: props.include_qr,
  initialStyle: props.default_style,
})
const stickers = useStickerGeneration(props.formats)

function chooseItem(id: string) {
  router.visit(printPath({ item_id: id }))
}

function clearItem() {
  router.visit(printPath())
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <UTabs v-model="activeTab" :items="tabItems" color="neutral" variant="pill" />

    <template v-if="activeTab === 'label'">
      <p class="text-muted text-sm">
        Print a label linked to an existing item or container. Its QR code opens that inventory
        record.
      </p>

      <PrintItemPicker v-if="!label.item.value" @select="chooseItem" />

      <FormatPicker
        :model-value="label.style.value"
        :formats="label.formats.value"
        @update:model-value="label.setStyle"
      />

      <LabelForm
        :item="label.item.value"
        :text="label.text.value"
        :include-qr="label.includeQr.value"
        :quantity="label.quantity.value"
        :is-full="label.isFull.value"
        :media="label.selectedFormat.value?.media ?? ''"
        @update:text="label.setText"
        @update:include-qr="label.setIncludeQr"
        @update:quantity="label.setQuantity"
        @clear-item="clearItem"
        @reset="label.reset"
      />

      <LabelPreview
        :preview-url="label.previewUrl.value"
        :format-name="label.selectedFormat.value?.display_name ?? 'Label'"
        :quantity="label.quantity.value"
        :print-status="label.printStatus.value"
        :print-message="label.printMessage.value"
        :can-print="label.canPrint.value"
        @print="label.print"
      />
    </template>

    <template v-if="activeTab === 'stickers'">
      <p class="text-muted text-sm">
        Generate unassigned asset tags in bulk. Scan one later to link it to an inventory item.
      </p>

      <StickerPreview
        v-if="stickers.labels.value"
        :labels="stickers.labels.value"
        :style-value="stickers.selectedStyle.value"
        :layout-label="stickers.selectedFormat.value?.display_name ?? ''"
        :layout-sublabel="`${stickers.selectedFormat.value?.codes_per_label ?? 1}-up`"
        :media-label="stickers.selectedMediaLabel.value"
        :print-status="stickers.printStatus.value"
        :print-message="stickers.printMessage.value"
        @reset="stickers.resetLabels"
        @print-all="stickers.printAll"
        @print-label="stickers.printLabel"
      />

      <template v-else>
        <FormatPicker
          :model-value="stickers.selectedStyle.value"
          :formats="stickers.formats"
          @update:model-value="stickers.selectFormat"
        />

        <p class="text-muted text-sm">
          <span class="text-highlighted font-medium">Selected stock:</span>
          {{ stickers.selectedFormat.value?.media }}
        </p>

        <UFormField label="How many stickers?">
          <UInputNumber
            :model-value="stickers.count.value"
            :min="stickers.selectedFormat.value?.min_count"
            :max="stickers.selectedFormat.value?.max_count"
            :step="stickers.selectedFormat.value?.step"
            @update:model-value="stickers.setCount"
          />
        </UFormField>

        <UButton
          label="Generate"
          :icon="ICON.sticker"
          color="neutral"
          variant="solid"
          block
          :loading="stickers.generating.value"
          @click="stickers.generate"
        />
      </template>
    </template>
  </NarrowPage>
</template>
